import { DOMAttributes, DOMProps } from '@react-types/shared';
import React, { ElementType, ForwardedRef, forwardRef, ReactNode } from 'react';

import {
  commonStyleHandlers,
  ResponsiveCssProp,
  SapphireCommonStyleProps,
  SapphireStyleProps,
  StyleHandlers,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';
import { filterDOMProps } from 'react-aria/filterDOMProps';

export interface FlexStyleProps extends SapphireCommonStyleProps {
  /**
   * The direction in which to layout children. See
   * [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-direction).
   * @default 'row'
   */
  flexDirection?: ResponsiveCssProp<'flexDirection'>;
  /**
   * Whether to wrap items onto multiple lines. See
   * [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-wrap).
   * @default false
   */
  flexWrap?: ResponsiveCssProp<'flexWrap'>;
  /**
   *  CSS shorthand property specifies the direction of a flex container, as well as its wrapping
   * behavior. see [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/flex-flow)
   */
  flexFlow?: ResponsiveCssProp<'flexFlow'>;

  justifyContent?: ResponsiveCssProp<'justifyContent'>;
  alignItems?: ResponsiveCssProp<'alignItems'>;
  alignContent?: ResponsiveCssProp<'alignContent'>;

  /** The space to display between both rows and columns. See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/gap). */
  gap?: ResponsiveCssProp<'gap', 'size'>;
  /** The space to display between columns. See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/column-gap). */
  columnGap?: ResponsiveCssProp<'columnGap', 'size'>;
  /** The space to display between rows. See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/row-gap). */
  rowGap?: ResponsiveCssProp<'rowGap', 'size'>;

  padding?: ResponsiveCssProp<'padding', 'size'>;
  paddingLeft?: ResponsiveCssProp<'padding', 'size'>;
  paddingRight?: ResponsiveCssProp<'paddingRight', 'size'>;
  paddingTop?: ResponsiveCssProp<'paddingTop', 'size'>;
  paddingBottom?: ResponsiveCssProp<'paddingBottom', 'size'>;

  container?: ResponsiveCssProp<'container'>;
  containerType?: ResponsiveCssProp<'containerType'>;
  containerName?: ResponsiveCssProp<'containerName'>;
}
export interface FlexProps
  extends DOMProps,
    GlobalDomAttributes,
    Omit<
      DOMAttributes,
      'children' | 'style' | 'className' | 'dangerouslySetInnerHTML'
    >,
    SapphireStyleProps,
    FlexStyleProps {
  /** Children of the flex container. */
  children: ReactNode;
  /**
   * @default 'div'
   */
  elementType?: ElementType;
}

export const flexStylePropsHandler: StyleHandlers<FlexStyleProps> = {
  ...commonStyleHandlers,
  flexDirection: 'flexDirection',
  flexWrap: 'flexWrap',
  flexFlow: 'flexFlow',
  justifyContent: { styleProp: 'justifyContent', convertValue: flexAlignValue },
  alignItems: { styleProp: 'alignItems', convertValue: flexAlignValue },
  alignContent: { styleProp: 'alignContent', convertValue: flexAlignValue },
  gap: 'gap',
  columnGap: 'columnGap',
  rowGap: 'rowGap',
  padding: 'padding',
  paddingLeft: 'paddingLeft',
  paddingRight: 'paddingRight',
  paddingTop: 'paddingTop',
  paddingBottom: 'paddingBottom',
  container: 'container',
  containerType: 'containerType',
  containerName: 'containerName',
};

export const Flex = forwardRef(function Flex(
  props: FlexProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();
  const { children, elementType, ...otherProps } = props;
  const { styleProps, filteredProps } = useSapphireStyleProps<
    Omit<FlexProps, 'children'>,
    FlexStyleProps
  >(otherProps, flexStylePropsHandler);

  const style = {
    ...styleProps.style,
    display: 'flex',
  };

  const ElementType = elementType || 'div';

  return (
    <ElementType
      {...filteredProps}
      {...filterDOMProps(props, { global: true })}
      style={style}
      ref={ref}
      className={styleProps.className}
    >
      {children}
    </ElementType>
  );
});

/**
 * Normalize 'start' and 'end' alignment values to 'flex-start' and 'flex-end'
 * in flex containers for browser compatibility.
 */
function flexAlignValue(value: string): string {
  if (value === 'start') {
    return 'flex-start';
  }

  if (value === 'end') {
    return 'flex-end';
  }

  return value;
}
